import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { initAnalytics } from '@/services/firebase';

const STORAGE_KEY = 'klipr_cookie_consent';
const CONSENT_EXPIRY_MONTHS = 12;

interface CookieConsent {
  necessary: boolean;
  analytics: boolean;
  timestamp: string;
}

interface CookieConsentContextValue {
  consent: CookieConsent | null;
  hasConsented: boolean;
  acceptAll: () => void;
  rejectNonEssential: () => void;
  updateConsent: (analytics: boolean) => void;
  resetConsent: () => void;
}

const CookieConsentContext = createContext<CookieConsentContextValue | undefined>(undefined);

function isConsentExpired(timestamp: string): boolean {
  const consentDate = new Date(timestamp);
  const expiryDate = new Date(consentDate);
  expiryDate.setMonth(expiryDate.getMonth() + CONSENT_EXPIRY_MONTHS);
  return new Date() > expiryDate;
}

function getStoredConsent(): CookieConsent | null {
  if (typeof window === 'undefined') return null;

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;

    const consent = JSON.parse(stored) as CookieConsent;

    if (isConsentExpired(consent.timestamp)) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }

    return consent;
  } catch {
    return null;
  }
}

function saveConsent(consent: CookieConsent): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
}

export function CookieConsentProvider({ children }: { children: ReactNode }) {
  const [consent, setConsent] = useState<CookieConsent | null>(() => getStoredConsent());

  const hasConsented = consent !== null;

  // Initialize analytics if consent was previously given
  useEffect(() => {
    if (consent?.analytics) {
      initAnalytics();
    }
  }, [consent?.analytics]);

  const acceptAll = useCallback(() => {
    const newConsent: CookieConsent = {
      necessary: true,
      analytics: true,
      timestamp: new Date().toISOString(),
    };
    setConsent(newConsent);
    saveConsent(newConsent);
    initAnalytics();
  }, []);

  const rejectNonEssential = useCallback(() => {
    const newConsent: CookieConsent = {
      necessary: true,
      analytics: false,
      timestamp: new Date().toISOString(),
    };
    setConsent(newConsent);
    saveConsent(newConsent);
  }, []);

  const updateConsent = useCallback((analytics: boolean) => {
    const newConsent: CookieConsent = {
      necessary: true,
      analytics,
      timestamp: new Date().toISOString(),
    };
    setConsent(newConsent);
    saveConsent(newConsent);

    if (analytics) {
      initAnalytics();
    }
  }, []);

  const resetConsent = useCallback(() => {
    if (typeof window !== 'undefined') {
      localStorage.removeItem(STORAGE_KEY);
    }
    setConsent(null);
  }, []);

  return (
    <CookieConsentContext.Provider
      value={{
        consent,
        hasConsented,
        acceptAll,
        rejectNonEssential,
        updateConsent,
        resetConsent,
      }}
    >
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent(): CookieConsentContextValue {
  const context = useContext(CookieConsentContext);
  if (context === undefined) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider');
  }
  return context;
}
